import { BaseSqliteRepository } from './base-sqlite.repository';
import { SqliteQueryError } from '../../errors';
import type { ITagRepository } from '@app/domain/ports';
import type { Tag } from '@app/domain/entities';

interface TagRow {
  id: number;
  name: string;
  createdAt: number;
  updatedAt: number;
}

interface TagUsageRow {
  tagId: number;
  count: number;
}

export class TagRepository extends BaseSqliteRepository implements ITagRepository {
  private readonly SELECT_CLAUSE = `
    SELECT _id as id, name, created_at as createdAt, updated_at as updatedAt
    FROM tags
  `;

  async findAll(): Promise<Tag[]> {
    const rows = await this.query<TagRow>(`${this.SELECT_CLAUSE} ORDER BY name ASC`);
    return rows.map(this.mapRow);
  }

  async getById(id: number): Promise<Tag | null> {
    const row = await this.queryOne<TagRow>(`${this.SELECT_CLAUSE} WHERE _id = ?`, [id]);
    return row ? this.mapRow(row) : null;
  }

  async insert(tag: Omit<Tag, 'id' | 'createdAt' | 'updatedAt'>): Promise<number> {
    const result = await this.queryOne<{ id: number }>(`INSERT INTO tags (name) VALUES (?) RETURNING _id AS id`, [
      tag.name,
    ]);
    if (!result?.id) {
      throw new SqliteQueryError('INSERT INTO tags', [tag.name], 'Failed to insert tag');
    }
    return result.id;
  }

  async update(tag: Omit<Tag, 'createdAt' | 'updatedAt'>): Promise<void> {
    await this.execute(`UPDATE tags SET name = ?, updated_at = strftime('%s','now') WHERE _id = ?`, [tag.name, tag.id]);
  }

  async delete(id: number): Promise<void> {
    await this.execute(`DELETE FROM expense_tags WHERE tag_id = ?`, [id]);
    await this.execute(`DELETE FROM tags WHERE _id = ?`, [id]);
  }

  async countUsageByTagIds(tagIds: number[]): Promise<Record<number, number>> {
    const uniqueTagIds = Array.from(new Set(tagIds.filter((id) => Number.isInteger(id) && id > 0)));
    if (uniqueTagIds.length === 0) {
      return {};
    }

    const placeholders = uniqueTagIds.map(() => '?').join(', ');
    const rows = await this.query<TagUsageRow>(
      `SELECT et.tag_id as tagId, COUNT(*) as count
       FROM expense_tags et
       INNER JOIN expenses e ON e._id = et.expense_id
       WHERE et.tag_id IN (${placeholders}) AND e.is_deleted = 0
       GROUP BY et.tag_id`,
      uniqueTagIds,
    );

    const counts: Record<number, number> = {};
    for (const id of uniqueTagIds) {
      counts[id] = 0;
    }
    for (const row of rows) {
      counts[row.tagId] = row.count;
    }

    return counts;
  }

  private mapRow(row: TagRow): Tag {
    return {
      id: row.id,
      name: row.name,
      createdAt: new Date(row.createdAt * 1000),
      updatedAt: new Date(row.updatedAt * 1000),
    };
  }
}
